import { T } from '@tolgee/react';
import { Modal } from 'antd';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ChatSvg } from '../../../assets/icons/ChatSvg';
import { CuriLightbulb } from '../../../assets/icons/CuriLightbulb';
import { CuriOpenArms } from '../../../assets/icons/CuriOpenArms';
import CuripodAIStarsSvg from '../../../assets/icons/CuripodAIStarsSvg';
import { CuriScoreCard } from '../../../assets/icons/CuriScoreCard';
import { DownloadSvg } from '../../../assets/icons/DownloadSvg';
import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import { ExternalLinkSvg } from '../../../assets/icons/ExternalLinkSvg';
import { HeartFilledSvg } from '../../../assets/icons/HeartFilledSvg';
import { LogoSvg } from '../../../assets/icons/LogoSvg';
import theme from '../../../assets/theme';
import { useWindowSize } from '../../../hooks/useWindowSize';
import Metrics from '../../../services/metrics';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import {
  resolveRegion,
  useTestPrepPaywallContent,
} from '../TestPrepLimitPaywall/testPrepPaywallContent';

interface PremiumTestPrepPlaywallModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  remaining: number;
  total: number;
  region?: string;
  country?: string;
  testName?: string;
}

type TabKey = 'why-upgrade' | 'how-to-get-access';

const MainContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.paleBeige};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 40px 20px;
  box-sizing: border-box;
  gap: 14px;
  max-width: 1000px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 20px 20px 16px;
    gap: 12px;
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const BadgePill = styled.div<{ $limitReached: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  background: ${({ $limitReached }) =>
    $limitReached ? 'rgba(226, 168, 22, 0.15)' : 'rgba(249, 151, 70, 0.15)'};
  color: ${({ $limitReached }) =>
    $limitReached ? theme.colors.yellow : theme.colors.orangeDark};
`;

const Title = styled.h2`
  margin: 0;
  padding: 0;
  font-family: 'Nunito', sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.black};
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const SubText = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${theme.colors.subtitleGrey};
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  width: 100%;
  max-width: 440px;
  border-bottom: 2px solid ${theme.colors.grey};
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  background: none;
  border: none;
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 700;
  padding: 10px 0;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  color: ${theme.colors.subtitleGrey};

  ${p =>
    p.$active &&
    css`
      color: ${theme.colors.orange};
      border-bottom-color: ${theme.colors.orange};
    `}

  &:hover {
    color: ${theme.colors.orange};
  }
`;

const TabContent = styled.div`
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatCard = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 10px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;

  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    padding: 14px;
  }
`;

const StatPercent = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 40px;
  font-weight: 800;
  color: ${theme.colors.orange};
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const StatLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
`;

const StatTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatHeadline = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
  line-height: 1.3;
`;

const StatSubtext = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 13px;
  color: ${theme.colors.subtitleGrey};
  line-height: 1.4;
  background: ${theme.colors.beigeDark};
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  width: fit-content;
`;

const CaseStudyLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 4px;
`;

const CaseStudyLink = styled.a`
  font-family: 'Nunito', sans-serif;
  font-size: 13px;
  color: ${theme.colors.orange};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
    color: ${theme.colors.orange};
  }
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.black};
`;

const FeatureIconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(249, 151, 70, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.orange};

  svg {
    width: 30px;
    height: 30px;
  }
`;

const IntroText = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${theme.colors.greyVeryDark};
  margin: 0;
  text-align: left;

  strong {
    font-weight: 700;
  }
`;

const NotAloneCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  padding: 20px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NotAloneTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.colors.orange};
`;

const NotAloneTitle = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
`;

const NotAloneText = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${theme.colors.greyVeryDark};
  margin: 0;

  strong {
    font-weight: 700;
    color: ${theme.colors.black};
  }
`;

const StepsTitle = styled.h4`
  font-family: 'Nunito', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 8px 0 4px;
  text-align: left;
`;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  padding: 2px 0;
`;

const StepNumber = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  color: #fff;
  font-family: 'Nunito', sans-serif;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const StepText = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${theme.colors.greyVeryDark};

  strong {
    font-weight: 700;
    color: ${theme.colors.black};
  }
`;

const WeCanHelpCard = styled.div`
  background: ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 16px 20px;
  text-align: left;
`;

const WeCanHelpTitle = styled.h4`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 8px;
`;

const WeCanHelpItem = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${theme.colors.greyVeryDark};
  margin: 0 0 2px;

  &::before {
    content: '\u2022 ';
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 520px;
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterLink = styled.span`
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  color: ${theme.colors.orange};
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const FooterDivider = styled.span`
  color: ${theme.colors.grey};
  font-size: 14px;
`;

export const PremiumTestPrepPlaywallModal: FunctionComponent<PremiumTestPrepPlaywallModalProps> = ({
  visible,
  onClose,
  onConfirm,
  remaining,
  total,
  region,
  country,
  testName,
}) => {
  const [windowWidth] = useWindowSize();
  const { showNewMessages } = useIntercom();
  const [activeTab, setActiveTab] = useState<TabKey>('why-upgrade');

  const handleClose = useCallback(() => {
    onClose();
    setActiveTab('why-upgrade');
  }, [onClose]);

  const paywallRegion = useMemo(() => resolveRegion(region, country), [region, country]);
  const { whyUpgrade, howToGetAccess } = useTestPrepPaywallContent(paywallRegion);

  const hasReachedLimit = remaining <= 0;
  const displayTestName = testName || 'Test Prep';
  const used = Math.max(0, total - remaining);

  const featureIcons = useMemo(
    () => [
      <CuriOpenArms key="openarms" />,
      <CuriScoreCard key="scorecard" />,
      <CuriLightbulb key="lightbulb" />,
    ],
    [],
  );

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      footer={null}
      width={windowWidth < 600 ? '95%' : 640}
      closable={true}
      maskClosable={true}
      className="premium-testprep-playwall-modal"
      style={{ top: 20 }}
      bodyStyle={{
        padding: 0,
        backgroundColor: theme.colors.paleBeige,
        borderRadius: '10px',
      }}
    >
      <MainContainer>
        <HeaderGroup>
          <BadgePill $limitReached={hasReachedLimit}>
            <EnterprisePlanIcon
              width={18}
              height={18}
              color={hasReachedLimit ? theme.colors.yellow : theme.colors.orangeDark}
            />
            <T
              keyName="PremiumTestPrepPlaywall.Badge"
              defaultValue="School Plan Test Prep"
            />
          </BadgePill>

          <Title>
            {hasReachedLimit ? (
              <T
                keyName="PremiumTestPrepPlaywall.Title.LimitReached"
                defaultValue="You've used all 3 Free School Plan {displayTestName} Teaching Sessions!"
                params={{ displayTestName }}
              />
            ) : (
              <T
                keyName="PremiumTestPrepPlaywall.Title.SessionsRemaining"
                defaultValue="You have {remaining} {displayTestName} Teaching {remaining, plural, one {Session} other {Sessions}} Left!"
                params={{ remaining, displayTestName }}
              />
            )}
          </Title>

          <SubText>
            {hasReachedLimit ? (
              <T
                keyName="PremiumTestPrepPlaywall.Subtitle.LimitReached"
                defaultValue="Upgrade to the School Plan for unlimited {displayTestName} lessons."
                params={{ displayTestName }}
              />
            ) : (
              <>
                <T
                  keyName="PremiumTestPrepPlaywall.Body.FreePlanNote.Usage"
                  defaultValue="You've used {used} of your 3 free school plan test prep teaching sessions."
                  params={{ used }}
                />{' '}
                <T
                  keyName="PremiumTestPrepPlaywall.Body.FreePlanNote.SchoolPlan"
                  defaultValue="Question types with a"
                />{' '}
                <EnterprisePlanIcon
                  color={hasReachedLimit ? theme.colors.yellow : theme.colors.black}
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    width: '16px',
                    height: '16px',
                  }}
                />{' '}
                <T
                  keyName="PremiumTestPrepPlaywall.Body.FreePlanNote.SchoolPlanSuffix"
                  defaultValue="are part of the School Plan — question types without a crown is on the free plan and counts towards your weekly teaching sessions."
                />
              </>
            )}
          </SubText>
        </HeaderGroup>

        <TabBar>
          <Tab
            $active={activeTab === 'why-upgrade'}
            onClick={() => {
              setActiveTab('why-upgrade');
              Metrics.getLogger().logEvent('PremiumTestPrepPlaywall.TabClicked', {
                tab: 'why-upgrade',
                region: paywallRegion,
              });
            }}
          >
            <T
              keyName="PremiumTestPrepPlaywall.Tab.WhyUpgrade"
              defaultValue="Why Upgrade"
            />
          </Tab>
          <Tab
            $active={activeTab === 'how-to-get-access'}
            onClick={() => {
              setActiveTab('how-to-get-access');
              Metrics.getLogger().logEvent('PremiumTestPrepPlaywall.TabClicked', {
                tab: 'how-to-get-access',
                region: paywallRegion,
              });
            }}
          >
            <T
              keyName="PremiumTestPrepPlaywall.Tab.HowToGetAccess"
              defaultValue="How to Get Access"
            />
          </Tab>
        </TabBar>

        {activeTab === 'why-upgrade' && (
          <TabContent>
            <StatCard>
              <StatPercent>
                {whyUpgrade.statPercent}
                <StatLabel>higher</StatLabel>
              </StatPercent>
              <StatTextGroup>
                <StatHeadline>{whyUpgrade.statHeadline}</StatHeadline>
                <StatSubtext>{whyUpgrade.statSubtext}</StatSubtext>
                {whyUpgrade.caseStudies.length > 0 && (
                  <CaseStudyLinks>
                    {whyUpgrade.caseStudies.map((cs, i) => (
                      <CaseStudyLink
                        key={i}
                        href={cs.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          Metrics.getLogger().logEvent(
                            'PremiumTestPrepPlaywall.CaseStudyClicked',
                            {
                              url: cs.url,
                              region: paywallRegion,
                            },
                          );
                        }}
                      >
                        <IconWrapper
                          color={theme.colors.orange}
                          height="12px"
                          width="12px"
                          cursor="pointer"
                        >
                          <ExternalLinkSvg />
                        </IconWrapper>
                        {cs.label}
                      </CaseStudyLink>
                    ))}
                  </CaseStudyLinks>
                )}
              </StatTextGroup>
            </StatCard>

            {whyUpgrade.features.map((feature, i) => (
              <FeatureRow key={i}>
                <FeatureIconCircle>
                  {featureIcons[i] ?? featureIcons[0]}
                </FeatureIconCircle>
                {feature}
              </FeatureRow>
            ))}
          </TabContent>
        )}

        {activeTab === 'how-to-get-access' && (
          <TabContent>
            <IntroText dangerouslySetInnerHTML={{ __html: howToGetAccess.intro }} />

            <NotAloneCard>
              <NotAloneTitleRow>
                <IconWrapper
                  color={theme.colors.orange}
                  height="16px"
                  width="16px"
                  cursor="default"
                >
                  <HeartFilledSvg />
                </IconWrapper>
                <NotAloneTitle>{howToGetAccess.notAloneTitle}</NotAloneTitle>
              </NotAloneTitleRow>
              <NotAloneText
                dangerouslySetInnerHTML={{ __html: howToGetAccess.notAlone }}
              />
            </NotAloneCard>

            <StepsTitle>
              <T
                keyName="PremiumTestPrepPlaywall.WhatHasWorked"
                defaultValue="What has worked for teachers like you:"
              />
            </StepsTitle>

            {howToGetAccess.steps.map((step, i) => (
              <StepRow key={i}>
                <StepNumber>{i + 1}</StepNumber>
                <StepText>
                  <strong>{step.title}</strong> — {step.description}
                </StepText>
              </StepRow>
            ))}

            <WeCanHelpCard>
              <WeCanHelpTitle>{howToGetAccess.weCanHelpTitle}</WeCanHelpTitle>
              {howToGetAccess.weCanHelp.map((item, i) => (
                <WeCanHelpItem key={i}>{item}</WeCanHelpItem>
              ))}
            </WeCanHelpCard>
          </TabContent>
        )}

        <CTAGroup>
          {!hasReachedLimit && onConfirm && (
            <Button
              type="orange"
              onClick={() => {
                Metrics.getLogger().logEvent(
                  'PremiumTestPrepPlaywall.StartSessionConfirmed',
                  {
                    remaining,
                    region: paywallRegion,
                  },
                );
                onConfirm();
              }}
              width="100%"
              styles={{
                borderRadius: '24px',
                maxWidth: 520,
                padding: '12px 24px',
                fontSize: 15,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CuripodAIStarsSvg style={{ width: 24, height: 24, flexShrink: 0 }} />
                <T
                  keyName="PremiumTestPrepPlaywall.StartSession"
                  defaultValue="Start Session"
                />
              </div>
            </Button>
          )}

          <a
            href="/school-plan-help"
            target="_blank"
            rel="noopener noreferrer"
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Button
              type={hasReachedLimit ? 'orange' : 'ghost'}
              onClick={() => {
                Metrics.getLogger().logEvent('PremiumTestPrepPlaywall.BookCall', {
                  remaining,
                  region: paywallRegion,
                  hasReachedLimit,
                });
              }}
              width="100%"
              styles={{
                borderRadius: '24px',
                maxWidth: 520,
                padding: '12px 24px',
                fontSize: 15,
              }}
            >
              <T
                keyName="PremiumTestPrepPlaywall.NeedAccessNow"
                defaultValue="I need access now — get help"
              />
            </Button>
          </a>

          {activeTab === 'how-to-get-access' && (
            <FooterLinks>
              <FooterLink
                onClick={() => {
                  Metrics.getLogger().logEvent('PremiumTestPrepPlaywall.ChatWithUs', {
                    region: paywallRegion,
                  });
                  showNewMessages();
                }}
              >
                <IconWrapper
                  color={theme.colors.orange}
                  height="14px"
                  width="14px"
                  cursor="pointer"
                >
                  <ChatSvg />
                </IconWrapper>
                <T
                  keyName="PremiumTestPrepPlaywall.ChatWithUs"
                  defaultValue="Chat with us"
                />
              </FooterLink>

              <FooterDivider>|</FooterDivider>

              <FooterLink
                as="a"
                href="/curipod_teacher_pitch.pdf"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  Metrics.getLogger().logEvent(
                    'PremiumTestPrepPlaywall.DownloadOnePager',
                    {
                      region: paywallRegion,
                    },
                  );
                }}
              >
                <IconWrapper
                  color={theme.colors.orange}
                  height="14px"
                  width="14px"
                  cursor="pointer"
                >
                  <DownloadSvg />
                </IconWrapper>
                <T
                  keyName="PremiumTestPrepPlaywall.DownloadOnePager"
                  defaultValue="Download 1-pager for admin"
                />
              </FooterLink>
            </FooterLinks>
          )}

          {activeTab === 'why-upgrade' && (
            <FooterLink
              onClick={() => {
                setActiveTab('how-to-get-access');
                Metrics.getLogger().logEvent(
                  'PremiumTestPrepPlaywall.HowToGetFullAccess',
                  {
                    region: paywallRegion,
                  },
                );
              }}
            >
              <T
                keyName="PremiumTestPrepPlaywall.HowToGetFullAccess"
                defaultValue="How to get full access?"
              />
            </FooterLink>
          )}
        </CTAGroup>
      </MainContainer>
    </Modal>
  );
};
